import * as React from "react";
import { useState } from "react";
import { EventCalendarTranslations } from "./EventCalendarTranslations.csharp";
import { PictureProfile } from "../../../../ResizedImage/PictureProfile.csharp";
import { ExhibitionItemViewModel } from "../../../Blocks/ExhibitionsListBlock/ExhibitionItemViewModel.csharp";
import { ICardViewModel } from "../../../Partials/Card/ICardViewModel.csharp";
import { NewExhibitionCard } from "../../../Partials/NewExhibitionCard/NewExhibitionCard";
import { CheckBox } from "../../Ecommerce/Shared/Checkbox/Checkbox";
import { CardsList } from "../../../Blocks/CardsList/CardsList";
import { CardsListVariant } from "../../../Blocks/CardsList/CardsListVariant.csharp";

export interface ExhibitionsListProps {
    currentExhibitions: ExhibitionItemViewModel[];
    futureExhibitions: ExhibitionItemViewModel[];
    pastExhibitions: ICardViewModel[];
    profile: PictureProfile;
    translations: EventCalendarTranslations;
    allExhibitionsUrl: string;
    collectionExhibitionsCheckboxText: string;
    temporaryExhibitionsCheckboxText: string;
}

export const ExhibitionsList = (props: ExhibitionsListProps) => {
    const [showCollectionExhibitions, setShowCollectionExhibitions] = useState(true);
    const [showTemporaryExhibitions, setShowTemporaryExhibitions] = useState(true);

    const filterExhibitions = (exhibitions: ExhibitionItemViewModel[]) => {
        return exhibitions.filter((exhibition) => {
            if (exhibition.isTemporary) {
                return showTemporaryExhibitions;
            } else {
                return showCollectionExhibitions;
            }
        });
    };

    const renderExhibitionCards = (exhibitions: ExhibitionItemViewModel[]) => {
        const filteredExhibitions = filterExhibitions(exhibitions);
        return filteredExhibitions.map((ex) => (
            <div className="ExhibitionsList__card-wrapper" key={ex.pageUrl}>
                <NewExhibitionCard
                    key={ex.pageUrl}
                    title={ex.title}
                    subtitle={ex.subtitle}
                    image={ex.image}
                    defaultProfile={props.profile}
                    shortDescription={ex.shortDescription}
                    floor={ex.floor}
                    exhibitionType={ex.exhibitionType}
                    isComingSoon={ex.isComingSoon}
                    comingDateText={ex.isComingSoon ? `Kommer ${ex.comingDateText}` : null}
                    pageUrl={ex.pageUrl}
                    theme={ex.theme}
                    customThemeBackgroundColor={ex.customThemeBackgroundColor}
                    customThemeFontColor={ex.customThemeFontColor}
                    isTemporary={ex.isTemporary}
                    cardTranslations={ex.cardTranslations}
                    duration={ex.duration}
                />
            </div>
        ));
    };

    const filteredCurrentExhibitions = filterExhibitions(props.currentExhibitions);
    const filteredFutureExhibitions = filterExhibitions(props.futureExhibitions);
    const pastExhibitionCards = props.pastExhibitions;

    // Create translations object like in ExhibitionsOverview
    const cardsListTranslations = {
        showMore: props.translations.showMore,
        showLess: "Vis mindre",
        card: props.translations.cardsList.card,
    };

    const hasAnyExhibitions = filteredCurrentExhibitions.length > 0 || filteredFutureExhibitions.length > 0;

    return (
        <div className="ExhibitionsList">
            <div className="ExhibitionsList__filters">
                <CheckBox
                    label={props.collectionExhibitionsCheckboxText ?? "Samlingsutstillinger"}
                    isChecked={showCollectionExhibitions}
                    setIsChecked={setShowCollectionExhibitions}
                />
                <CheckBox
                    label={props.temporaryExhibitionsCheckboxText ?? "Midlertidige utstillinger"}
                    isChecked={showTemporaryExhibitions}
                    setIsChecked={setShowTemporaryExhibitions}
                />
            </div>

            {hasAnyExhibitions ? (
                <>
                    {filteredCurrentExhibitions.length > 0 && (
                        <div className="ExhibitionsList__section">
                            {renderExhibitionCards(props.currentExhibitions)}
                        </div>
                    )}

                    {filteredFutureExhibitions.length > 0 && (
                        <>
                            <h2 className="ExhibitionsList__sectionTitle">{props.translations.futureExhibitions}</h2>
                            <div className="ExhibitionsList__section">
                                {renderExhibitionCards(props.futureExhibitions)}
                            </div>
                        </>
                    )}
                </>
            ) : (
                <p>{props.translations.noExhibitionsForFilters}</p>
            )}

            {pastExhibitionCards.length > 0 && (
                <div className="ExhibitionOverview__pastExhibitions">
                    <div>
                        <CardsList
                            defaultProfile={props.profile}
                            featuredProfile={props.profile}
                            translations={cardsListTranslations}
                            variant={CardsListVariant.List}
                            className="ExhibitionOverview__pastExhibitionList"
                            animateChildren={true}
                            model={{
                                alwaysExpanded: true,
                                cards: pastExhibitionCards,
                                title: props.translations.pastExhibitions,
                                showFeatured: false,
                                hideLink: false,
                            }}
                        />
                    </div>
                </div>
            )}

            <a href={props.allExhibitionsUrl} className="EventCalendar__button button--secondary">
                {props.translations.showAllExhibitions}
            </a>
        </div>
    );
};
